import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  NOTISTACK_FAILURE_OPTIONS,
  NOTISTACK_SUCCESS_OPTIONS,
} from "constants/index";
import {
  useListStateInfoQuery,
  StateInfo,
  useUpdateStateInfoMutation,
} from "features/trading/accounts";
import { useSnackbar } from "notistack";
import { getStateLabel } from "components/ui/StateSelect";

const MAX_TAX_RATE_PERCENT = 100;

/**
 * A rate of exactly 0 is a deliberate answer — several states levy no income
 * tax — so only an absent rate counts as unconfigured. The API sends the
 * decimal back as a string ("0.0000"), which is why this tests the raw value
 * rather than its number form.
 */
const hasTaxRate = (state: Pick<StateInfo, "tax_rate">) =>
  state.tax_rate !== null &&
  state.tax_rate !== undefined &&
  String(state.tax_rate).trim() !== "";

const CONFIGURED_LABEL = "Tax rate set";
const UNCONFIGURED_LABEL = "No tax rate set";

const getTaxRateValidationError = (value: string) => {
  if (value !== "" && Number(value) < 0) {
    return "Tax rate cannot be negative.";
  }
  if (value !== "" && Number(value) >= MAX_TAX_RATE_PERCENT) {
    return "Tax rate cannot be greater than 100%.";
  }
  return null;
};

const StateTaxRates = () => {
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [stateInputValue, setStateInputValue] = useState("");
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [taxRate, setTaxRate] = useState<string>("");
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError } = useListStateInfoQuery({
    pagination: { page: 0, pageSize: 100 },
    sorting: [{ field: "state_name", sort: "asc" }],
  });
  const [updateStateInfo, { isLoading: isSaving }] =
    useUpdateStateInfoMutation();

  const states = data?.results ?? [];

  const selectedState = useMemo(
    () => states.find((state) => state.id === selectedStateId) ?? null,
    [selectedStateId, states],
  );

  useEffect(() => {
    if (!hasAutoSelected && selectedStateId === null && states.length > 0) {
      setSelectedStateId(states[0].id);
      setHasAutoSelected(true);
    }
  }, [hasAutoSelected, selectedStateId, states]);

  useEffect(() => {
    if (selectedStateId === null) {
      setTaxRate("");
      setStateInputValue("");
      return;
    }
    setTaxRate(selectedState?.tax_rate ?? "");
    if (selectedState) {
      setStateInputValue(getStateLabel(selectedState));
    }
  }, [selectedStateId, selectedState]);

  const handleStateChange = (
    _event: React.SyntheticEvent,
    nextValue: StateInfo | null,
  ) => {
    setSelectedStateId(nextValue?.id ?? null);
    setStateInputValue(nextValue ? getStateLabel(nextValue) : "");
  };

  const handleStateInputChange = (
    _event: React.SyntheticEvent,
    nextInputValue: string,
  ) => {
    setStateInputValue(nextInputValue);
  };

  const saveChange = async () => {
    if (!selectedState) {
      return;
    }
    const normalizedRate = taxRate.trim();
    const validationError = getTaxRateValidationError(normalizedRate);
    if (validationError) {
      enqueueSnackbar(validationError, NOTISTACK_FAILURE_OPTIONS as any);
      return;
    }
    try {
      await updateStateInfo({
        id: selectedState.id,
        tax_rate: normalizedRate === "" ? null : normalizedRate,
      }).unwrap();
      enqueueSnackbar("Tax rate saved.", NOTISTACK_SUCCESS_OPTIONS as any);
    } catch {
      enqueueSnackbar(
        "Failed to save state tax rate.",
        NOTISTACK_FAILURE_OPTIONS as any,
      );
    }
  };

  const isDirty = selectedState
    ? String(selectedState.tax_rate ?? "") !== taxRate
    : false;

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        State Tax Rates
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Set tax rates by state abbreviation and name.
      </Typography>
      {isLoading ? (
        <CircularProgress />
      ) : isError ? (
        <Alert severity="error">Failed to load states.</Alert>
      ) : (
        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2} maxWidth={520}>
              <Autocomplete
                options={states}
                getOptionLabel={getStateLabel}
                value={selectedState}
                inputValue={stateInputValue}
                onInputChange={handleStateInputChange}
                onChange={handleStateChange}
                renderInput={(params) => (
                  <TextField {...params} label="State" size="small" />
                )}
                renderOption={(optionProps, option) => {
                  const isConfigured = hasTaxRate(option);
                  const StatusIcon = isConfigured ? CheckCircleIcon : CancelIcon;
                  return (
                    <Box
                      component="li"
                      {...optionProps}
                      key={option.id}
                      sx={{ display: "flex", gap: 1.5 }}
                    >
                      <StatusIcon
                        color={isConfigured ? "success" : "error"}
                        fontSize="small"
                        // The icon is the only carrier of this status, so it
                        // has to reach a screen reader as text too.
                        titleAccess={
                          isConfigured ? CONFIGURED_LABEL : UNCONFIGURED_LABEL
                        }
                        sx={{ flexShrink: 0 }}
                      />
                      {getStateLabel(option)}
                    </Box>
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                clearOnEscape
              />
              <TextField
                size="small"
                label="Tax Rate"
                type="number"
                inputProps={{ step: "0.0001", min: "0" }}
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                sx={{ width: 220 }}
                disabled={!selectedState}
              />
              <Box>
                <Button
                  variant="contained"
                  onClick={saveChange}
                  disabled={!isDirty || isSaving || !selectedState}
                >
                  Save Tax Rate
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StateTaxRates;
